import { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { seoArticles } from '@/mocks/articles';
import { articleContents } from '@/mocks/article-content';
import { articleImages } from '@/mocks/images';
import { mainSiteUrl } from '@/mocks/categories';

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (slug === 'vigorbuy-spreadsheet-guide') {
      navigate('/vigorbuy-spreadsheet-guide', { replace: true });
      return;
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug, navigate]);

  const meta = seoArticles.find((a) => a.slug === slug);
  const content = slug ? articleContents[slug] : null;
  const heroImage = slug ? articleImages[slug] : '';

  if (!meta || !content) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
        <Navbar />
        <main className="flex-1 pt-16 flex items-center justify-center">
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'oklch(var(--accent-100))' }}>
              <i className="ri-emotion-sad-line text-2xl" style={{ color: 'oklch(var(--accent-600))' }}></i>
            </div>
            <h1 className="font-heading font-semibold text-xl mb-2 text-foreground-950">Article Not Found</h1>
            <p className="text-sm text-foreground-600 mb-6">The article you are looking for does not exist or has been moved.</p>
            <Link to="/" className="btn-primary text-sm px-6 py-2.5 rounded-lg">
              <i className="ri-home-line mr-2"></i>
              Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
      <Navbar />
      <main className="flex-1 pt-16">
        {/* Article Hero */}
        <section className="py-12 md:py-16" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
          <div className="section-container max-w-3xl">
            <div className="flex items-center gap-2 text-xs mb-4 text-foreground-500">
              <Link to="/" className="hover:text-primary-500 transition-colors duration-200">Home</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700">Article</span>
            </div>

            {/* Article hero image */}
            <div className="w-full aspect-[2/1] rounded-lg overflow-hidden mb-6">
              <img
                src={heroImage}
                alt={meta.title}
                className="w-full h-full object-cover"
              />
            </div>

            <h1 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl mb-4 leading-tight text-foreground-950">
              {meta.title}
            </h1>
            <p className="text-sm md:text-base leading-relaxed mb-4 text-foreground-600">
              {meta.description}
            </p>
            <div className="flex items-center gap-3 text-xs text-foreground-500">
              <span>Updated: {meta.date}</span>
              <span className="w-1 h-1 rounded-full" style={{ backgroundColor: 'oklch(var(--foreground-400))' }}></span>
              <span>{meta.readTime}</span>
            </div>
          </div>
        </section>

        {/* Article Body */}
        <article className="py-10 md:py-14">
          <div className="section-container max-w-3xl">
            {content.sections.map((section, idx) => (
              <section key={idx} className="mb-10">
                <h2 className="font-heading font-semibold text-lg md:text-xl mb-4 text-foreground-950">
                  {section.heading}
                </h2>
                {section.paragraphs.map((para, pi) => (
                  <p
                    key={pi}
                    className="text-sm md:text-base leading-relaxed mb-4 text-foreground-700"
                    dangerouslySetInnerHTML={{ __html: para }}
                  />
                ))}
              </section>
            ))}

            {/* Related internal links */}
            <section className="mb-10 p-6 rounded-lg" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
              <h3 className="font-heading font-semibold text-base mb-3 text-foreground-950">
                Explore More VigorBuy Spreadsheet Resources
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {seoArticles
                  .filter((a) => a.slug !== slug)
                  .slice(0, 6)
                  .map((a) => (
                    <Link
                      key={a.slug}
                      to={`/article/${a.slug}`}
                      className="text-sm py-2 px-3 rounded-md transition-colors duration-200 hover:bg-primary-50 text-foreground-700 hover:text-primary-600"
                    >
                      <i className="ri-arrow-right-line mr-1.5 text-xs"></i>
                      {a.title.length > 55 ? a.title.slice(0, 55) + '...' : a.title}
                    </Link>
                  ))}
              </div>
            </section>

            {/* CTA */}
            <section className="mb-8 p-6 md:p-8 rounded-lg text-center" style={{ backgroundColor: 'oklch(var(--primary-50))' }}>
              <h3 className="font-heading font-semibold text-lg mb-2 text-primary-800">
                {content.ctaTitle}
              </h3>
              <p className="text-sm mb-5 text-foreground-700">
                {content.ctaDescription}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="btn-primary text-sm px-8 py-3 rounded-lg"
                >
                  <i className="ri-external-link-line mr-2"></i>
                  VigorBuy Spreadsheet
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center px-8 py-3 rounded-lg font-semibold text-sm whitespace-nowrap cursor-pointer transition-all duration-200 border-2"
                  style={{
                    borderColor: 'oklch(var(--primary-300))',
                    color: 'oklch(var(--primary-600))',
                  }}
                >
                  <i className="ri-home-line mr-2"></i>
                  Back to Home
                </Link>
              </div>
            </section>
          </div>
        </article>
      </main>

      <Footer />

      {/* Article Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: content.schemaHeadline,
            description: content.schemaDescription,
            author: {
              '@type': 'Organization',
              name: 'VigorBuy Guide',
            },
            datePublished: meta.date,
            dateModified: meta.date,
            publisher: {
              '@type': 'Organization',
              name: 'VigorBuy Guide',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': import.meta.env.VITE_SITE_URL + '/article/' + slug,
            },
          }),
        }}
      />
    </div>
  );
}